export function getPropertyManagerIdsFromProperty(property) {
  if (!property) return [];
  if (Array.isArray(property.propertyManagerIds) && property.propertyManagerIds.length > 0) {
    return [...new Set(property.propertyManagerIds.map((id) => String(id)))];
  }
  if (property.propertyManagerId) {
    return [String(property.propertyManagerId)];
  }
  return [];
}

function uniqueManagers(managers = []) {
  const seen = new Set();
  return managers.filter((manager) => {
    if (!manager) return false;
    const key = manager.id != null
      ? String(manager.id)
      : getPropertyManagerDisplayName(manager);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function getTeamPropertyManagers(team) {
  if (!team) return [];
  const fromUserTeams = (team.userTeams || []).map((membership) => membership.user);
  return uniqueManagers([...(team.members || []), ...fromUserTeams]);
}

export function getPropertyManagers(property) {
  if (!property) return [];
  if (Array.isArray(property.propertyManagers) && property.propertyManagers.length > 0) {
    return property.propertyManagers;
  }
  const teamManagers = getTeamPropertyManagers(property.propertyManagementTeam);
  if (teamManagers.length > 0) {
    return teamManagers;
  }
  if (property.propertyManager) {
    return [property.propertyManager];
  }
  return [];
}

export function getUsersOnPropertyManagementTeam(users = [], teamId) {
  if (!teamId) return [];
  const normalizedTeamId = String(teamId);
  return users.filter((user) => {
    if (!user || user.isActive === false) return false;
    if (String(user.teamId) === normalizedTeamId) return true;
    return Array.isArray(user.userTeams)
      && user.userTeams.some((membership) => String(membership.teamId) === normalizedTeamId);
  });
}

const PROPERTY_MANAGEMENT_NAME_MATCH_ROLES = new Set([
  'PROPERTY_MANAGER',
  'GUEST_APP_MANAGER',
  'ADMIN',
  'HOUSEKEEPER_MANAGER',
]);

export function getUsersMatchingTeamName(users = [], teamName) {
  if (!teamName) return [];
  const tokens = String(teamName)
    .split('/')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length < 2) return [];
  return tokens.flatMap((token) => {
    const matches = users.filter((user) => {
      if (!user || user.isActive === false) return false;
      const firstName = String(user.firstName || '').trim().toLowerCase();
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase();
      return firstName === token || fullName === token;
    });
    if (matches.length <= 1) return matches;
    const preferred = matches.filter((user) => PROPERTY_MANAGEMENT_NAME_MATCH_ROLES.has(user.role));
    return preferred.length > 0 ? preferred : matches;
  });
}

export function enrichPropertyManagersWithUsers(managers = [], users = []) {
  const usersById = new Map(users.map((user) => [String(user.id), user]));
  return managers.map((manager) => {
    const user = usersById.get(String(manager.id));
    if (!user) return manager;
    return {
      ...user,
      ...manager,
      phoneNumber: manager.phoneNumber || user.phoneNumber || null,
    };
  });
}

export function resolveDisplayPropertyManagers({
  property,
  users = [],
  teamMembers = [],
} = {}) {
  const teamId = property?.propertyManagementTeamId || property?.propertyManagementTeam?.id;
  const teamName = property?.propertyManagementTeam?.name;
  const merged = uniqueManagers([
    ...getPropertyManagers(property),
    ...teamMembers,
    ...getUsersOnPropertyManagementTeam(users, teamId),
  ]);
  const resolved = merged.length > 0
    ? merged
    : getUsersMatchingTeamName(users, teamName);
  return enrichPropertyManagersWithUsers(resolved, users);
}

export function getPropertyManagerDisplayName(manager) {
  if (!manager) return '';
  const fullName = `${manager.firstName || ''} ${manager.lastName || ''}`.trim();
  return manager.name || fullName || manager.email || '';
}

export function formatPropertyManagerNames(managers = []) {
  return managers.map(getPropertyManagerDisplayName).filter(Boolean).join(', ');
}

export function getPropertyManagementLabel(property) {
  if (property?.propertyManagementTeam?.name) {
    return property.propertyManagementTeam.name;
  }
  return formatPropertyManagerNames(getPropertyManagers(property));
}

export function parseManagementFilter(value) {
  if (!value) return {};
  if (value.startsWith('team:')) {
    const propertyManagementTeamId = parseInt(value.slice(5), 10);
    return Number.isFinite(propertyManagementTeamId)
      ? { propertyManagementTeamId }
      : {};
  }
  if (value.startsWith('user:')) {
    return { propertyManagerId: value.slice(5) };
  }
  return { propertyManagerId: value };
}
